import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { CookieConsent } from "@/components/CookieConsent";
import SessionTimeoutBanner from "@/components/SessionTimeoutBanner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { initGoogleAds } from "@/lib/gads";
import Login from "@/pages/Login";
import FirmLogin from "@/pages/FirmLogin";
import Dashboard from "@/pages/Dashboard";
import Workbench from "@/pages/Workbench";
import ReferPage from "@/pages/ReferPage";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Pipeline from "@/pages/Pipeline";
import DocumentsPage from "@/pages/DocumentsPage";
import InsightsPage from "@/pages/InsightsPage";
import ApprovalQueue from "@/pages/ApprovalQueue";
import AuditLogPage from "@/pages/AuditLogPage";
import Settings from "@/pages/Settings";
import Portal from "@/pages/Portal";
import RewardsPage from "@/pages/RewardsPage";
import LandingPage from "@/pages/LandingPage";
import LegalPage from "@/pages/LegalPage";
import ServiceDetail from "@/pages/ServiceDetail";
import ContentPage from "@/pages/ContentPage";
import TaxDataRoom from "@/pages/TaxDataRoom";
import DrakeMetricsDashboard from "@/pages/DrakeMetricsDashboard";
import { BlogList, BlogPost, DeadlinesList } from "@/blog/BlogPages";
import { useParams } from "react-router-dom";

function BlogPostRoute() {
  const { slug } = useParams();
  return <BlogPost slug={slug} />;
}

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } } });

function Protected({ children, firmOnly }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-sm text-zinc-400">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (firmOnly && user.role === "client") return <Navigate to="/portal" replace />;
  return children;
}

function ClientOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-sm text-zinc-400">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "client") return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  useEffect(() => { initGoogleAds(); }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPostRoute />} />
            <Route path="/deadlines" element={<DeadlinesList />} />
            <Route path="/privacy-policy" element={<LegalPage />} />
            <Route path="/terms" element={<LegalPage />} />
            <Route path="/disclaimer" element={<LegalPage />} />
            <Route path="/client-portal" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/firm/login" element={<FirmLogin />} />
            <Route path="/firm" element={<Navigate to="/firm/login" replace />} />
            <Route path="/refer/:code" element={<ReferPage />} />
            <Route path="/portal" element={<ClientOnly><Portal /></ClientOnly>} />
            <Route path="/portal/rewards" element={<ClientOnly><RewardsPage /></ClientOnly>} />
            <Route element={<Protected firmOnly><Layout /></Protected>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/workbench" element={<Workbench />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/clients/:id" element={<ClientDetail />} />
              <Route path="/pipeline" element={<Pipeline />} />
              <Route path="/documents" element={<DocumentsPage />} />
              <Route path="/insights" element={<InsightsPage />} />
              <Route path="/approvals" element={<ApprovalQueue />} />
              <Route path="/audit" element={<AuditLogPage />} />
              <Route path="/content" element={<ContentPage />} />
              <Route path="/settings" element={<Settings />} />
            </Route>
            <Route path="/engagements/:engagementId/data-room" element={<Protected firmOnly><TaxDataRoom /></Protected>} />
            <Route path="/drake/metrics" element={<Protected firmOnly><DrakeMetricsDashboard /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <SessionTimeoutBanner />
        <Toaster position="bottom-right" richColors />
        <CookieConsent />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
